/* eslint-disable no-use-before-define */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { type ChartView } from 'echarts';
import { graphic } from 'echarts/core';
import { type BoundingRect } from '../../../model/util/coord';

/** 旗子标记对应的信息 */
export interface FlagLayoutInfo {
  /** 旗子标记的方框对应的zrender元素 */
  rect: graphic.Rect;
  /** 旗子标记的文本对应的zrender元素 */
  label: graphic.Text;
  /** 旗子标记的旗杆对应的zrender元素 */
  line: graphic.Line;
  /** 旗子标记的整体容器对应的zrender元素 */
  group: graphic.Group;
  /** 旗子标记的圆点对应的zrender元素 */
  circle: graphic.Circle;
  /** 旗子标记的文本和方框所在容器对应的zrender元素 */
  insideGroup: graphic.Group;
  /** 旗子标记的文本和方框对应的包围矩形的信息 */
  boundRect: BoundingRect;
  /** 旗子标记的显示优先级 */
  weight: number;
}
type FlagInfo = Pick<FlagLayoutInfo, 'rect' | 'insideGroup' | 'line' | 'boundRect' | 'group'> & {
  rightOverlapList?: FlagInfo[];
  shifted?: boolean;
};
type FlagGroup = graphic.Group & {
  flag_group_inside?: graphic.Group;
  flag_rect?: graphic.Rect;
  flag_text?: graphic.Text;
  flag_line?: graphic.Line;
  flag_circle?: graphic.Circle;
};

/**
 * 用于拆分代码的内部方法
 * @remarks 主要功能是根据旗子的group元素，解构出旗子的各项信息，并添加到旗子信息列表中
 */
function addFlagInfo(out: FlagInfo[], flagItem: FlagGroup) {
  const { flag_rect: rect, flag_line: line, flag_group_inside: insideGroup } = flagItem;
  const boundRect: BoundingRect = {
    x: insideGroup.x,
    y: insideGroup.y,
    height: rect.shape.height,
    width: rect.shape.width
  };
  out.push({
    rect,
    line,
    insideGroup,
    boundRect,
    group: flagItem
  });
}
/**
 * 预处理旗子信息的列表
 */
export function prepareFlagInfoList(chartView: ChartView) {
  const { group } = chartView;
  // 定义旗子各个组成部分的ID的标志性字符信息
  const flagPartNames = ['flag_group_inside', 'flag_rect', 'flag_text', 'flag_line', 'flag_circle'];
  // 用于存储处理完成的旗子信息的列表
  const flagInfoList: FlagInfo[] = [];
  // 遍历标记系列视图View下的每一个旗子
  group.eachChild((flagItem: FlagGroup) => {
    // 对于每一个旗子的group元素，遍历其全部后代元素（包含所有的叶子节点和枝干节点）
    flagItem.traverse(el => {
      // 如果当前元素已经被标记为忽略，则跳过
      if (el.ignore) {
        return;
      }
      // 对于当前元素，依次判别是旗子的哪一个组成部分，如果判定出是具体某部分，则将当前元素挂载到旗子的最外层group上
      flagPartNames.forEach(name => {
        if ((el.extra.dvid as string).indexOf(name) !== -1) {
          flagItem[name] = el;
        }
      });
    });

    // 如果当前旗子没有忽略，则将当前旗子的信息加入到旗子信息列表
    if (!flagItem.ignore) {
      addFlagInfo(flagInfoList, flagItem);
    }
  });

  return flagInfoList;
}

// 布局所有标记的位置，不再有重叠的情况
export function shiftLayout(
  list: FlagInfo[],
  minBound: number,
  maxBound: number
  // balanceShift?: boolean
) {
  const len = list.length;

  // 定义无需重新布局的标记数量阈值，一般2个以下的标记不需要重新排布
  const SHIFT_THRESHOLD = 2;
  if (len < SHIFT_THRESHOLD) {
    return;
  }

  // 将标记列表按照水平方向顺序重新排序，具体根据标记的左边距顺序排序
  list.sort((a, b) => {
    // 因为旗子标记的宽度可能为负值，所以这里获取标记的严格左边界和严格有边界
    const leftA = Math.min(a.boundRect.x, a.boundRect.x + a.boundRect.width);
    const leftB = Math.min(b.boundRect.x, b.boundRect.x + b.boundRect.width);
    return leftA - leftB;
  });

  // 遍历标记列表
  for (let i = 0; i < len; i += 1) {
    const item = list[i];
    // 取当前标记的包围盒信息
    const itemBound = item.boundRect;
    // 获取当前标记的右重叠标记列表
    const rightOverlapList = getRightOverlapList(itemBound.x, itemBound.x + itemBound.width);
    // 记录在本标记中
    item.rightOverlapList = rightOverlapList;
  }

  // 对于每个标记，进行一次重叠位置调整
  for (let i = 0; i < len; i += 1) {
    shiftOverlap(list[i]);
  }

  /**
   * 获取[min,max]区间的右重叠标记列表
   * @remarks [min, max]的右重叠列表（存在重叠，并位于[min, max]右边）
   * @returns
   */
  function getRightOverlapList(min: number, max: number) {
    // 获取严格的左边界和右边界
    const left = Math.min(min, max);
    const right = Math.max(min, max);
    // 记录右重叠标记的列表
    const overlapList: FlagInfo[] = [];

    // 判定是否左右重叠的阈值, 小于该值则认为没有重叠，这样是为了尽可能多的利用图表空间
    const overlapThreshold = 0.1;
    // 遍历标记列表
    for (let i = 0; i < len; i += 1) {
      const item = list[i];
      const itemBound = item.boundRect;
      // 获取当前标记的严格左边界
      const itemLeft = Math.min(itemBound.x, item.boundRect.x + itemBound.width);
      // 获取右重叠集合
      if (itemLeft - left > 0 && right - itemLeft > overlapThreshold) {
        overlapList.push(item);
      }
    }
    return overlapList;
  }

  /**
   * 对标记进行位置调整，对于超出的标记进行隐藏
   *
   * @returns
   */
  function shiftOverlap(item: FlagInfo) {
    if (item == null || item.shifted) {
      return;
    }
    // 竖直方向上流出的富余间隔
    const ySpace = 2;
    // 记录当前标记能够移动到的最靠上的位置， 默认为图表上边界
    let lastPos = minBound;
    // 如果当前标记存在右重叠列表
    if (item.rightOverlapList.length) {
      // 遍历右重叠列表
      for (let i = 0; i < item.rightOverlapList.length; i += 1) {
        const rightFlag = item.rightOverlapList[i];
        // 对右重叠中的每一个标记进行位置调整
        shiftOverlap(rightFlag);
        // 如果列表中某一标记没有被隐藏，更新lastPos
        if (!rightFlag.insideGroup.ignore) {
          // lastPos更新为没被隐藏的标记的下边界和lastPos的较大值
          lastPos = Math.max(rightFlag.insideGroup.y + rightFlag.boundRect.height, lastPos);
        }
      }
    }

    const { insideGroup, line, boundRect, rect } = item;
    // 当lastPos不足以容纳当前标记，则直接隐藏该标记
    if (lastPos + ySpace + boundRect.height > maxBound) {
      item.group.attr('ignore', true);
    } else {
      // 对当前标记进行调整
      // 旗子方框和文本调整到lastPos再往下留出一定间隔
      insideGroup.attr({
        y: lastPos + ySpace
      });

      const { lineYEnd, rectBorderRadius } = shiftStyles(insideGroup, line, boundRect, rect);

      // 旗杆保持和方框正确连接
      line.attr({
        shape: {
          y2: lineYEnd
        }
      });
      rect.attr({
        shape: {
          r: rectBorderRadius
        }
      });

      /** 旗子方框的位置调整后，调整旗杆y方向上的终点以及旗子方框的圆角样式
       * @description 旗子方框与旗杆连接处圆角为0
       *  */
      function shiftStyles(insideGroup, line, boundRect, rect) {
        const { y: flagGroupY } = insideGroup;
        const { height: boundHeight } = boundRect;
        const { y1: lineYStart } = line.shape;
        const rectWidth = rect.shape.width;
        let lineYEnd = lineYStart;
        // 圆角数组元素依次对应【左上，右上，右下，左下】
        let rectBorderRadius = Array.isArray(rect.shape.r) ? rect.shape.r : [rect.shape.r, rect.shape.r, rect.shape.r, rect.shape.r];


        if (flagGroupY > lineYStart) {
          // 如果旗子方框上边界的y坐标 > 旗杆起始点y坐标, 即旗子原点在旗子上方, 则正常连到旗子；
          // 旗子方框与旗杆连接处在上方
          lineYEnd = flagGroupY;
          if (rectWidth > 0) {
            rectBorderRadius[0] = 0
          } else {
            rectBorderRadius[1] = 0
          }
        } else if (flagGroupY + Math.abs(boundHeight) > lineYStart) {
          // 否则，如果旗子方框上边界的y坐标 < 旗杆起始点y坐标 < 旗子方框下边界的y坐标, 即旗子原点在旗子内部, 则旗杆长度为0
          // 旗子方框与旗杆连接处旗子方框内部，原本的连接处一侧的圆角需要置为0
          lineYEnd = line.shape.y1;
          if (rectWidth > 0) {
            rectBorderRadius[0] = 0
            rectBorderRadius[3] = 0
          } else {
            rectBorderRadius[1] = 0
            rectBorderRadius[2] = 0
          }
        } else {
          // 否则，如果旗子方框下边界的y坐标 < 旗杆起始点y坐标 即旗子原点在旗子下, 则旗杆连接到旗子下边界
          // 旗子方框与旗杆连接处在下方
          lineYEnd = insideGroup.y + Math.abs(boundRect.height);
          if (rectWidth > 0) {
            rectBorderRadius[3] = 0
          } else {
            rectBorderRadius[2] = 0
          }
        }

        return { lineYEnd, rectBorderRadius };
      }
    }
    // 完成对当前标记的调整
    item.shifted = true;
  }
}

export function shiftLayoutOnY(
  list: Pick<FlagLayoutInfo, 'rect' | 'insideGroup' | 'line' | 'boundRect' | 'group'>[],
  topBound: number,
  bottomBound: number
  // balanceShift?: boolean
): void {
  return shiftLayout(list, topBound, bottomBound);
}
